import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

interface DrawerContextProps {
  drawerOpen: boolean;
  menuOpen: boolean;
  toggleDrawer: () => void;
  closeDrawer: () => void;
  openDrawer: () => void;
}

const DrawerContext = createContext<DrawerContextProps | undefined>(undefined);

const MOBILE_QUERY = '(max-width:600px)';
const DESKTOP_DRAWER_STORAGE_KEY = 'desktopDrawerOpen';

const getStoredDesktopDrawerState = () => {
  if (typeof window === 'undefined') {
    return true;
  }

  const storedValue = window.localStorage.getItem(DESKTOP_DRAWER_STORAGE_KEY);

  if (storedValue === null) {
    return true;
  }

  return storedValue === 'true';
};

const isMobileViewport = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.matchMedia(MOBILE_QUERY).matches;
};

export const DrawerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = useState<boolean>(() => (
    isMobileViewport() ? false : getStoredDesktopDrawerState()
  ));
  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    const handleViewportChange = (event: MediaQueryListEvent | MediaQueryList) => {
      if (event.matches) {
        setDrawerOpen(false);
        setMenuOpen(false);
        return;
      }

      setDrawerOpen(getStoredDesktopDrawerState());
      setMenuOpen(false);
    };

    handleViewportChange(mediaQuery);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleViewportChange);

      return () => {
        mediaQuery.removeEventListener('change', handleViewportChange);
      };
    }

    mediaQuery.addListener(handleViewportChange);

    return () => {
      mediaQuery.removeListener(handleViewportChange);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || isMobileViewport()) {
      return;
    }

    window.localStorage.setItem(DESKTOP_DRAWER_STORAGE_KEY, String(drawerOpen));
  }, [drawerOpen]);

  const toggleDrawer = () => {
    if (isMobileViewport()) {
      setMenuOpen(prev => !prev);
      return;
    }

    setDrawerOpen(prev => !prev);
  };

  const closeDrawer = () => {
    if (isMobileViewport()) {
      setMenuOpen(false);
      return;
    }

    setDrawerOpen(false);
  };

  const openDrawer = () => {
    if (isMobileViewport()) {
      setMenuOpen(true);
      return;
    }

    setDrawerOpen(true);
  };

  return (
    <DrawerContext.Provider value={{ drawerOpen, menuOpen, toggleDrawer, closeDrawer, openDrawer }}>
      {children}
    </DrawerContext.Provider>
  );
};

export const useDrawer = (): DrawerContextProps => {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error('useDrawer must be used within a DrawerProvider');
  }
  return context;
};
